import { useDebounce } from "@lyfegen-platform/react-common";
import { Box } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useSimulationsPersistedFilters } from "../../../components/filters/models";
import LyfeToolbar from "../../../components/LyfeTable/LyfeToolbar";
import TableBottomDrawer from "../../../components/LyfeTable/TableBottomDrawer";
import { MS_UNTIL_QUERY_CALL } from "../../../constants/util-constants";
import { HeaderActionOptions } from "../../../models/common/data";
import { bottomDrawerState } from "../../../store/layout";
import {
	addSearchTermCondition,
	filterStateFamily,
	getFieldNameByColumnName,
	getSortOrderByHeaderAction,
	simulationsTableHeaderActionsState,
} from "../../../store/lyfe-data-table";
import { pageSizeOptions, simulationsActivePage, simulationsRowsPerPage, snackbarUpdateStatus } from "../../../store/simulator";
import { userPersonalInfoState } from "../../../store/user";
import { GET_FILTERED_SIMULATION_COUNT, GET_SIMULATIONS, SIMULATION_SUBSCRIPTION, UPDATE_SIMULATION_FILTER } from "../../data/queries";
import { GetSimulationsQuery } from "../../data/types/queries.generated";
import { UPDATE_SIMULATIONS_TABLE_COLUMNS } from "../../user/queries";
import { ViewSwitcher } from "../components/ViewSwitcher";
import { AddButton } from "../components/AddButton";
import { SimulationTable } from "../components/SimulationTable";
import ErrorSnackbarAlert from "../../../components/errorMessages/ErrorSnackbarAlert";
import { useTranslation } from "react-i18next";
import { modelsModuleString, useModelsMutation, useModelsQuery } from "../../../hooks/useModelsModule";
import SubscriptionForModelsUpdates from "../../../hooks/subscription-for-models-updates";

const defaultSort = {
	column: "id",
	action: "sortDesc" as HeaderActionOptions,
};

export const simulationTableColumns = [
	{
		title: "simulation-id",
		field: "id",
		default: true,
	},
	{
		title: "product",
		field: "product",
		default: true,
	},
	{
		title: "status",
		field: "status",
		default: true,
	},
	{
		title: "short-name",
		field: "shortName",
		default: true,
	},
	{
		title: "domain-header-general",
		field: "domainName",
		default: false,
	},
	{
		title: "simulation-counterparty",
		field: "counterparty",
		default: true,
	},
	{
		title: "simulator-header-indications",
		field: "indications",
		default: false,
	},
	{
		title: "model",
		field: "model",
		default: true,
	},
	{
		title: "last-update",
		field: "lastUpdatedOn",
		default: false,
	},
	{
		title: "added-by",
		field: "createdByName",
		default: false,
	},
];

interface IListViewProps {
	comparisonSimulations: Array<string>;
}

export const ListView = (props: IListViewProps) => {
	const { t } = useTranslation();
	const title = "simulations";
	const [searchTerm, setSearchTerm] = useState<string>("*");
	const rowsPerPage = useRecoilValue(simulationsRowsPerPage);
	const { loading: filtersLoading, filters: filtersInitialState, refetchFilters } = useSimulationsPersistedFilters();
	const filters = useRecoilValue(filterStateFamily(title));
	const page = useRecoilValue(simulationsActivePage);
	const actionState = useRecoilValue(simulationsTableHeaderActionsState);
	const debouncedSearchTerm = useDebounce(searchTerm, MS_UNTIL_QUERY_CALL);
	const setFilteredSimulationsErrorSnackbarUpdateStatus = useSetRecoilState(snackbarUpdateStatus);

	const { data, loading, error, refetch } = useModelsQuery<GetSimulationsQuery>(GET_SIMULATIONS, {
		variables: {
			where: { ...addSearchTermCondition(filters, debouncedSearchTerm) },
			options: {
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				sort: {
					[getFieldNameByColumnName(actionState.column || defaultSort.column).trimEnd()]: getSortOrderByHeaderAction(
						actionState.action || defaultSort.action
					)?.trimEnd(),
				},
			},
		},
		onError: (error) => {
			setFilteredSimulationsErrorSnackbarUpdateStatus((prevState) => ({
				...prevState,
				hasError: true,
			}));
			console.error("Error Filtering Simulations:", error);
		},
		skip: !filters,
		fetchPolicy: "no-cache",
	});

	const handleRefetch = () => {
		refetch();
		refetchFilters();
	};
	const bottomDrawer = useSetRecoilState(bottomDrawerState);
	const [user, setUser] = useRecoilState(userPersonalInfoState);
	const [update] = useModelsMutation(UPDATE_SIMULATIONS_TABLE_COLUMNS);
	const filteredRecordsCount = data?.filteredSimulationCount[0]._count;

	useEffect(() => {
		const pendingColumns: Array<string> = filters?.AND?.map((filter: any) => Object.keys(filter)[0]).filter(Boolean);
		if (pendingColumns?.length) {
			const columns = [...user.simulationsTableColumns, ...pendingColumns];
			setUser({
				...user,
				simulationsTableColumns: columns,
			});
			update({ variables: { simulationsColumns: columns } });
		}
	}, [filters]);

	const totalRecordsCount = data?.allSimulationCount?.[0]?._count || 0;

	const handleSearch = (input: string) => {
		setSearchTerm(input || "");
	};

	useEffect(() => {
		bottomDrawer(
			<TableBottomDrawer
				title={title}
				totalRecords={totalRecordsCount || 0}
				filteredRecords={filteredRecordsCount || 0}
				pageSizeOptions={pageSizeOptions}
			/>
		);
		return () => bottomDrawer(null);
	}, [filteredRecordsCount, pageSizeOptions, totalRecordsCount, filteredRecordsCount]);

	return (
		<>
			<SubscriptionForModelsUpdates onCompleted={handleRefetch} query={SIMULATION_SUBSCRIPTION} />
			{!filtersLoading && (
				<LyfeToolbar
					title={title}
					buttons={[<AddButton />]}
					viewButtons={[
						<Box width="100%" display="flex" justifyContent={"space-between"}>
							<ViewSwitcher numberOfSimulations={props.comparisonSimulations.length} />
						</Box>,
					]}
					filteredRecords={filteredRecordsCount || 0}
					totalRecords={totalRecordsCount || 0}
					handleSearch={handleSearch}
					filterInitialState={filtersInitialState}
					tableColumns={simulationTableColumns}
					saveFilterQuery={UPDATE_SIMULATION_FILTER}
					module={modelsModuleString}
					countQuery={GET_FILTERED_SIMULATION_COUNT}
				/>
			)}
			{error && (
				<ErrorSnackbarAlert
					errorTitle={t("simulator-filter-update-error-p1")}
					secondaryText={t("simulator-filter-update-error-p2")}
				/>
			)}
			<SimulationTable comparisonSimulations={props.comparisonSimulations} data={data} loading={loading || filtersLoading} />
		</>
	);
};
